import {
  IPolicy,
  IPolicyPreviewResponse,
  IPolicyResponse,
  IPolicyResult,
  IPolicyUpdateRequest,
} from 'shared';

import { CacheTags } from '../../types/cacheTags';
import { orgHeader } from '../../../../common/lib/axiosBaseQuery';
import { emptySplitApi } from '../baseApi';

export interface IUpdatePolicy extends IPolicyUpdateRequest {
  organisationId: string;
  id: number;
}

type IPreviewPolicyBase = {
  organisationId: string;
  policyId: number;
  /** Which contact person the preview renders for */
  contactPersonId?: string;
  /** The full selection the send will fan out to, so the preview can list every recipient */
  contactPersonIds?: string[];
};

export type IPreviewPolicyRequest =
  | (IPreviewPolicyBase & {
    invoiceId: string;
    contactId: number;
    quoteId?: string;
  })
  | (IPreviewPolicyBase & {
    quoteId: string;
    invoiceId?: never;
    contactId?: never;
  });

export const policyApi = emptySplitApi.injectEndpoints({
  endpoints: builder => ({
    fetchPolicies: builder.query<IPolicyResponse, string>({
      query: org => ({
        url: `policy`,
        method: `GET`,
        headers: {
          [orgHeader]: org,
        },
      }),
      providesTags: result => result ?
        [
          ...result.policies.map(({ policy }) => ({ type: CacheTags.Policies, id: policy.id })),
          { type: CacheTags.Policies, id: `LIST` },
        ]
        : [{ type: CacheTags.Policies, id: `LIST` }],
    }),
    createPolicy: builder.mutation({
      query: ({ organisationId, type, groupId, defaultOptions }) => {
        return ({
          url: `policy`,
          method: `POST`,
          headers: {
            [orgHeader]: organisationId,
          },
          data: {
            policy_type: type,
            groupId,
            defaultOptions,
          },
        });
      },
      invalidatesTags: [
        { type: CacheTags.Policies, id: `LIST` },
        { type: CacheTags.Organisations, id: `LIST` }, // To update usage,
        // { type: CacheTags.Groups, id: `LIST` }, // We know refetch manually at call site so we know when its complete
        { type: CacheTags.Actions, id: `LIST` },
        { type: CacheTags.ActionCreateStatus, id: `LIST` },
        { type: CacheTags.EmailTemplates, id: `LIST` },
      ],
    }),
    updatePolicy: builder.mutation<unknown, IUpdatePolicy>({
      query: ({ organisationId, id, ...update }) => ({
        url: `policy/${id}`,
        method: `PATCH`,
        headers: {
          [orgHeader]: organisationId,
        },
        data: {
          ...update,
          policy: {
            ...update.policy,
            completedActions: undefined,
          },
        },
      }),
      invalidatesTags: ({ id }) => [
        { type: CacheTags.Policies, id },
        { type: CacheTags.Groups, id: `LIST` },
        { type: CacheTags.Actions, id: `LIST` },
        { type: CacheTags.ActionCreateStatus, id: `LIST` },
        { type: CacheTags.EmailTemplates, id: `LIST` },
        { type: CacheTags.Organisations, id: `LIST` }, // To update system policies
        { type: CacheTags.SystemPolicies, id: `LIST` },
      ],
    }),
    suggestPolicyTitle: builder.mutation<{ title: string }, { organisationId: string; id: number }>({
      query: ({ organisationId, id }) => ({
        url: `policy/${id}/suggest-title`,
        method: `POST`,
        headers: {
          [orgHeader]: organisationId,
        },
      }),
    }),
    updateSystemPolicy: builder.mutation<unknown, IUpdatePolicy>({
      query: ({ organisationId, id, ...update }) => ({
        url: `policy/system/${id}`,
        method: `PATCH`,
        headers: {
          [orgHeader]: organisationId,
        },
        data: {
          ...update,
          policy: {
            ...update.policy,
            completedActions: undefined,
          },
        },
      }),
      invalidatesTags: ({ id }) => [
        { type: CacheTags.Policies, id },
        { type: CacheTags.Groups, id: `LIST` },
        { type: CacheTags.Actions, id: `LIST` },
        { type: CacheTags.ActionCreateStatus, id: `LIST` },
        { type: CacheTags.EmailTemplates, id: `LIST` },
        { type: CacheTags.Organisations, id: `LIST` }, // To update system policies
        { type: CacheTags.SystemPolicies, id: `LIST` },
      ],
    }),
    deletePolicy: builder.mutation({
      query: ({ organisationId, id }) => ({
        url: `policy/${id}`,
        method: `DELETE`,
        headers: {
          [orgHeader]: organisationId,
        },
      }),
      invalidatesTags: () => [
        { type: CacheTags.Policies, id: `LIST` },
        // { type: CacheTags.Groups, id: `LIST` }, // We know refetch manually at call site so we know when its complete
        { type: CacheTags.Organisations, id: `LIST` }, // To update usage,
        { type: CacheTags.Actions, id: `LIST` },
        { type: CacheTags.ActionCreateStatus, id: `LIST` },
        { type: CacheTags.EmailTemplates, id: `LIST` },
      ],
    }),
    copyPolicy: builder.mutation<{ policy: IPolicy }, { organisationId: string; id: number; groupId: number }>({
      query: ({ organisationId, id, groupId }) => ({
        url: `policy/${id}/copy`,
        method: `POST`,
        headers: {
          [orgHeader]: organisationId,
        },
        data: { groupId },
      }),
      invalidatesTags: [
        { type: CacheTags.Policies, id: `LIST` },
        { type: CacheTags.Groups, id: `LIST` },
      ],
    }),
    previewPolicy: builder.query<IPolicyPreviewResponse, IPreviewPolicyRequest>({
      query: ({ organisationId, policyId, invoiceId, contactId, contactPersonId, contactPersonIds, quoteId }) => ({
        url: `policy/${policyId}/preview`,
        method: `GET`,
        headers: {
          [orgHeader]: organisationId,
        },
        params: {
          invoiceId,
          contactId,
          contactPersonId,
          contactPersonIds: contactPersonIds?.join(`,`),
          quoteId,
        },
      }),
      providesTags: [
        { type: CacheTags.PreviewPolicy, id: `LIST` },
      ],
    }),
    checkPolicyForQuote: builder.query<IPolicyResult, { organisationId: string; policyId: number; quoteId: string | number }>({
      query: ({ organisationId, policyId, quoteId }) => ({
        url: `policy/${policyId}/quote-check`,
        method: `GET`,
        headers: {
          [orgHeader]: organisationId,
        },
        params: { quoteId },
      }),
      providesTags: [
        { type: CacheTags.PreviewPolicy, id: `LIST` },
      ],
    }),
  }),
});

export const {
  useFetchPoliciesQuery,
  useCreatePolicyMutation,
  useDeletePolicyMutation,
  useCopyPolicyMutation,
  useUpdatePolicyMutation,
  useLazyFetchPoliciesQuery,
  usePreviewPolicyQuery,
  useLazyPreviewPolicyQuery,
  useUpdateSystemPolicyMutation,
  useSuggestPolicyTitleMutation,
  useCheckPolicyForQuoteQuery,
} = policyApi;
